import { Component, Input, OnChanges, OnDestroy, OnInit } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NzModalService } from "ng-zorro-antd/modal";
import { RecipientFilterModalComponent } from "./recipient-filter-modal/recipient-filter-modal.component";
import { FilterOperatorEnum, BulkNotificationDto, NotificationBulkFilterDto, CommunicationPlaceholders, FileUploadDto, NotificationBulkDto } from "../../../service/dto";
import { HttpCommunicationService } from "../../../service/http-communication.service";
import { ScheduleModalComponent } from "./schedule-modal/schedule-modal.component";
import { catchError, filter, Subscription } from "rxjs";
import { NzMessageService } from "ng-zorro-antd/message";
import { CommunicationConfirmationModalComponent } from "./confirmation-modal/communication-confirmation-modal.component";
import { NzUploadChangeParam, NzUploadFile, NzUploadXHRArgs } from "ng-zorro-antd/upload";
import { HttpFileService } from "../../../service/http-file.service";
import { ActivatedRoute } from "@angular/router";

@Component({
	selector: 'app-communication-send',
	standalone: false,
	templateUrl: './communication-send.component.html',
	styleUrl: './communication-send.component.scss'
})
export class CommunicationSendComponent implements OnInit, OnDestroy {
	communicationForm!: FormGroup;
	smsForm!: FormGroup;
	emailForm!: FormGroup;
	deliveryForm!: FormGroup;

	placeholders!: CommunicationPlaceholders;

	selectedFilters: NotificationBulkFilterDto[] = [];

	filterCount: number = 0;
	filterOperator = FilterOperatorEnum;

	private subscription = new Subscription();

	editingBulkId!: number;
	bulkPayload!: BulkNotificationDto;

	uploadedFileList!: NzUploadFile[];

	constructor(
		private fb: FormBuilder, 
		private route: ActivatedRoute,
		private modalService: NzModalService, 
		private message: NzMessageService,
		private httpService: HttpCommunicationService,
		private fileService: HttpFileService) {
			
		this.communicationForm = this.fb.group({
			sendType: ['email', Validators.required]
		});

		this.communicationForm.get('sendType')?.valueChanges.subscribe((newValue) => {
			this.updateFilterCount(newValue);
		});

		this.smsForm = this.fb.group({
			message: ['', [Validators.required, Validators.maxLength(160)]]
		});

		this.emailForm = this.fb.group({
			subject: ['', Validators.required],
			message: ['', Validators.required]
		});
		this.deliveryForm = this.fb.group({
			schedule: [false],
			deliveryReport: [false]
		});
		this.deliveryForm.get('schedule')?.valueChanges.subscribe(checked => {
			this.onScheduleChange(checked);
		});
	}

	ngOnInit(): void {
		this.httpService.getPlaceholders()
			.pipe(
				catchError(error => {
					console.log('Error fetching placeholders', error);
					this.message.error('Failed to fetch placeholders');
					throw error;
				})
			)
			.subscribe(result => this.placeholders = result);
			
		this.route.paramMap.subscribe(params => {
			const id = params.get('id');

			if (id) {
				this.fetchBulk(+id);
			} else {
				this.bulkPayload = {
					bulk: {}
				};
			}
		});
	}

	fetchBulk(id: number) {
		this.httpService.get(id)
			.subscribe({
				next: (payload: BulkNotificationDto) => {
					if (!payload) {
						this.message.error('No such communication with ID ' + id);
						this.bulkPayload = {
							bulk: {}
						};
						return;
					}
					this.bulkPayload = payload;
					this.uploadedFileList = [];
					if (this.bulkPayload.bulk?.id) {
						this.editingBulkId = this.bulkPayload.bulk.id;
					}
					this.patchFormsFromPayload();
				},
				error: err => {
					this.message.error('Failed to load scheduled communication');
					console.error(err);
				}
			});
	}

	ngOnChanges(): void {
		if (this.bulkPayload) {
			this.patchFormsFromPayload();
		}
	}

	private patchFormsFromPayload(): void {
		const dto = this.bulkPayload;
		if (!dto) return;

		const channel = dto.bulk?.channel;
		const sendType = channel === 'Sms' ? 'sms' : 'email';
		this.communicationForm.patchValue({ sendType });

		if (dto.bulk) {
			const bulk: NotificationBulkDto = dto.bulk;
			if (bulk.subject != null) {
				this.emailForm.patchValue({ subject: bulk.subject });
			}
			if (bulk.body != null) {
				if (sendType === 'sms') {
					this.smsForm.patchValue({ message: bulk.body });
				} else {
					this.emailForm.patchValue({ message: bulk.body });
				}
			}

			this.deliveryForm.patchValue({
				deliveryReport: bulk.deliveryReport ?? false
			});

			if (this.bulkPayload.trigger) {
				this.deliveryForm.patchValue({ schedule: true }, { emitEvent: false });
			} else {
				this.deliveryForm.patchValue({ schedule: false }, { emitEvent: false });
			}
		}

		if (Array.isArray(dto.filters) && dto.filters.length > 0) {
			this.selectedFilters = dto.filters.map(f => ({ ...f }));
			this.updateFilterCount();
		} else {
			this.selectedFilters = [];
			this.filterCount = dto.filterCount ?? 0;
		}

		if (Array.isArray(dto.attachments) && dto.attachments.length > 0) {
			this.bulkPayload.attachments = dto.attachments.map(a => ({ ...a }));

			this.uploadedFileList = this.bulkPayload.attachments?.map(att => ({
				uid: att.uuid,         // unique id
				name: att.fileName,
				status: 'done',        // already uploaded
				response: att,         // store the attachment object for reference
			})) ?? [];
		}
	}

	openRecipientFilters() {
		const modal = this.modalService.create({
			nzTitle: 'Filter Recipients',
			nzContent: RecipientFilterModalComponent,
			nzWidth: '75%',
			nzFooter: null
		});

		modal.componentInstance!.existingFilters = this.selectedFilters!;

		modal.afterClose.subscribe(result => {
			if (result?.filters) {
				this.selectedFilters = result.filters;
				console.log('Selected filters:', this.selectedFilters);

				this.updateFilterCount();
			}
		});
	}

	removeFilter(index: number): void {
		this.selectedFilters.splice(index, 1);
		this.updateFilterCount();
	}

	isSms(): boolean {
		return this.communicationForm.value.sendType === 'sms';
	}

	isEmail(): boolean {
		return this.communicationForm.value.sendType === 'email';
	}

	updateFilterCount(sendType?: string) {
		const type = sendType ?? this.communicationForm.value.sendType;

		if (!this.selectedFilters) {
			this.filterCount = 0;
			return;
		}
		if (this.selectedFilters.length === 0) {
			this.filterCount = 0;
			return;
		}

		var payload: BulkNotificationDto = {
			bulk: {channel: type === 'sms' ? 'Sms' : 'Email'},
			filters: this.collectFilters(),
			filterCount: this.filterCount
		}
		this.httpService.getFilterCount(payload).subscribe(result => this.filterCount = result);
	}

	onScheduleChange(checked: boolean) {
		if (checked) {
			this.showSchedule();
		} else {
			this.clearSchedule();
		}
	}

	showSchedule() {
		const modal = this.modalService.create({
			nzTitle: 'Schedule Delivery',
			nzContent: ScheduleModalComponent,
			nzKeyboard: false,
			nzWidth: '40%',
			nzFooter: null
		});

		modal.componentInstance!.bulkPayload = this.bulkPayload!;

		modal.afterClose.subscribe(result => {
			if (result) {
				console.log('Schedule: ', result);
				this.bulkPayload = result;
			} else {
				if (!this.bulkPayload) {
					this.deliveryForm.get('schedule')?.setValue(false, { emitEvent: false });
				}
			}

			if (!this.bulkPayload.trigger) {
				this.deliveryForm.get('schedule')?.setValue(false, { emitEvent: false });
			}
		});
	}

	clearSchedule() {
		if (this.bulkPayload) {
			this.bulkPayload.trigger = undefined;
		}
		this.deliveryForm.get('schedule')?.setValue(false, { emitEvent: false });
	}

	save() {
		var subject = undefined;
		var message = undefined;
		if (this.communicationForm.value.sendType === 'sms') {
			if (this.smsForm.invalid) {
				this.message.error('Message is required and must be 160 characters or less');
				return;
			}
			message = this.smsForm.value.message;
		} else {
			if (!this.emailForm.value.subject) {
				this.message.error('Please enter the subject');
				return;
			}
			
			if (!this.emailForm.value.message) {
				this.message.error('Please enter the message');
				return;
			}

			subject = this.emailForm.value.subject;
			message = this.emailForm.value.message;
		}

		if(this.deliveryForm.get('schedule')!.value === true && !this.bulkPayload.trigger){
			this.message.error('Please specify a schedule');
			return;
		}

		if (this.bulkPayload.bulk) {
			this.bulkPayload.bulk.channel = this.communicationForm.value.sendType === 'sms' ? 'Sms' : 'Email';
			this.bulkPayload.bulk.deliveryReport = this.deliveryForm.get('deliveryReport')?.value;
			this.bulkPayload.bulk.subject = subject;
			this.bulkPayload.bulk.body = message;
		}

		if (this.bulkPayload.trigger && !this.bulkPayload.trigger.triggerDescription && this.bulkPayload.trigger.description) {
			this.bulkPayload.trigger.triggerDescription = this.bulkPayload.trigger.description;
		}

		this.bulkPayload.filters = this.selectedFilters.map(item => {
			var value = item.value;
			if (item.value.label) {
				value = item.value.label;
			}

			var out: NotificationBulkFilterDto = {
				propertyId: item.propertyId,
				propertyName: item.propertyName,
				operator: item.operator,
				dataType: item.dataType,
				value: value
			}
			return out;
		});

		const modal = this.modalService.create({
			nzTitle: 'Confirm Communication',
			nzContent: CommunicationConfirmationModalComponent,
			nzKeyboard: false,
			nzWidth: '60%',
			nzFooter: null
		});

		modal.componentInstance!.bulkPayload = this.bulkPayload;

		modal.afterClose.subscribe(result => {
			if (result) {
				this.doSave();
			}
		});
	}

	private doSave() {
		let payload = this.bulkPayload;
		if(this.deliveryForm.get('schedule')!.value === false){			
			payload = JSON.parse(JSON.stringify(this.bulkPayload));
			payload.trigger=undefined;
		}

		var observable = this.editingBulkId ? this.httpService.edit(payload) : this.httpService.add(payload);
		observable.pipe(
			catchError(error => {
				console.log('Error saving communication', error);
				this.message.error('Failed to save communication: ' + (error.error ? error.error.error : error.message));
				throw error;
			})
		)
		.subscribe(result => {
			this.message.success("Communication saved successfully");
			if (this.editingBulkId) {
				this.fetchBulk(this.editingBulkId);
			} else {
				this.bulkPayload = {};
				this.filterCount = 0;
				this.selectedFilters = [];
				this.uploadedFileList = [];
				this.communicationForm.reset();
				this.communicationForm.patchValue({sendType: 'email'});
				this.smsForm.reset();
				this.emailForm.reset();
				this.deliveryForm.reset();
			}
		});
	}

	get summaryFilterCount(): string {
		if (this.filterCount > 0) {
			return this.filterCount+'';
		}
		return '0 - No recipients found';
	}

	private collectFilters(): NotificationBulkFilterDto[] {
		return this.selectedFilters.map(item => {

			let value = item.value;

			if (this.editingBulkId) {
				return {
					...item,
					operator: item.operator ?? undefined,
					value
				};
			}

			if (item.dataType === 'DropDownSingle' || item.dataType === 'SelectFromRecords') {
				value = Array.isArray(item.value)
				? item.value.map(v => v.label)
				: item.value.label;
			}

			return {
				...item,
				operator: item.operator ?? undefined,
				value
  			};
		});
	}

	customUpload = (item: NzUploadXHRArgs): Subscription => {
		const formData = new FormData();
		formData.append('file', item.file as any);

		// Create the subscription
		const sub = this.fileService.upload(item.name ?? ('attachment-' + Date.now()), formData).subscribe({
			next: (res) => {
				// success callback
				item.onSuccess!(res, item.file, event);
				if (!this.bulkPayload.attachments) {
					this.bulkPayload.attachments = [];
				}
				this.bulkPayload.attachments.push(res);
			},
			error: (err) => {
				item.onError!(err, item.file);
			}
		});
		this.subscription.add(sub);

		// return it so nz-upload can cancel if needed
		return sub;
	};

	handleUploadChange(info: NzUploadChangeParam): void {
		if (info.type === 'removed') {
			const f = info.file;

			// Update UI file list
			this.uploadedFileList = info.fileList;

			// Update payload list
			if (this.bulkPayload.attachments) {
				this.bulkPayload.attachments = this.bulkPayload.attachments.filter(att => att.uuid !== f.uid);
			}
		}
	}

	ngOnDestroy(): void {
		if (this.subscription) {this.subscription.unsubscribe();}
	}
}